import { UniButton } from '@/design-system/components/buttons';
import { ButtonViewProps } from '@/design-system/components/buttons/UniButton';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { Dropdown } from '@/design-system/components/dropdown';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniInput } from '@/design-system/components/inputs';
import { ModalHeader, useModalView } from '@/design-system/components/modal';
import { H6, TextBody, TextCaption } from '@/design-system/components/typography';
import { useNavigation } from '@react-navigation/native';
import { LayoutContext } from 'contexts/LayoutContext';
import { Pressable, SxProp, View } from 'dripsy';
import { isString } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStackRoutes } from 'types';
import { UnionType } from '@/features/bill-payment/enum';
import { BillPaymentStackRoutes } from '@/features/bill-payment/payment.navigator';
import { RemittancePurposeSelectFilters, FundSourceSelectFilters } from '@/features/bill-payment/screens/union-pay/options';
import { UnionPayStackRoutes } from '@/features/bill-payment/screens/union-pay/type';

export interface InformationWebScreenProps {
  onLayout: (event: any) => void;
  onBack?: (flag?: boolean) => void;
  transferPeer?: any;
  wallet?: any;
  calcFee?: any;
  onChangeView?: () => void;
  transferPeerCountFlag?: boolean;
  unionType?: UnionType.PAYMENT | UnionType.ACCOUNT;
  refresh?: () => void;
  navigationNavigate?: string;
  navigationScreen?: string;
}

export const InformationWebScreen: React.FC<InformationWebScreenProps> = (props) => {
  const {
    onLayout,
    onBack,
    transferPeer,
    wallet,
    calcFee,
    onChangeView,
    transferPeerCountFlag = true,
    unionType,
    refresh,
    navigationNavigate = RootStackRoutes.BillPayment,
    navigationScreen = BillPaymentStackRoutes.UnionPay
  } = props;
  const { t } = useTranslation('bill-payment-union-pay');
  const navigation = useNavigation();
  const navigationAny = useNavigation<any>();
  const { useModalProps } = useModalView({});
  const [buttonProps, setButtonProps] = useState<Partial<ButtonViewProps>>({
    size: 'default',
    variant: ButtonVariant.primary,
    label: 'Button Label'
  });
  const handleChangeButtonProps = useCallback((key: keyof ButtonViewProps, value: ButtonViewProps[keyof ButtonViewProps]) => {
    setButtonProps((prev) => ({ ...prev, [key]: value }));
  }, []);

  const [accountData, setAccountData] = useState(transferPeer);

  const [beneficiaryInformation, setBeneficiaryInformation] = useState<{
    account: string | undefined;
    purpose: string | undefined;
    fundSource: string | undefined;
    remark: string | undefined;
  }>({
    account: undefined,
    purpose: undefined,
    fundSource: undefined,
    remark: undefined
  });
  useEffect(() => {
    setBeneficiaryInformation({ ...beneficiaryInformation, account: transferPeer });
  }, []);
  const [errorMsg, setErrorMsg] = useState<{ account: string | undefined; purpose: string | undefined; fundSource: string | undefined }>({
    account: undefined,
    purpose: undefined,
    fundSource: undefined
  });
  const validateSubmit = () => {
    if (!beneficiaryInformation.account) {
      setErrorMsg({ ...errorMsg, account: t('unionPay::payment-instruction.field-required') });
    } else if (!beneficiaryInformation.purpose) {
      setErrorMsg({ ...errorMsg, purpose: t('unionPay::payment-instruction.field-required') });
    } else if (!beneficiaryInformation.fundSource) {
      setErrorMsg({ ...errorMsg, fundSource: t('unionPay::payment-instruction.field-required') });
    } else {
      navigationAny.navigate(navigationNavigate, {
        screen: navigationScreen,
        params: {
          screen: UnionPayStackRoutes.Confirmation,
          onLayout,
          onBack,
          wallet,
          calcFee,
          beneficiaryInformation,
          onChangeView: onChangeViewLocal,
          unionType,
          refresh
        }
      });
    }
  };

  const layout = useContext(LayoutContext);

  const [viewShow, setViewShow] = useState(false);
  const onChangeViewLocal = () => {
    setViewShow(!viewShow);
  };

  const onCancel = () => {
    onBack && onBack();
    onChangeView && onChangeView();
  };

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <View sx={sxStyles.modalHeader}>
        <ModalHeader
          title={t('unionPay::payment-instruction.glossary.unionpay')}
          closeModal={() => {
            onBack && onBack(true);
          }}
        />
      </View>
      <View sx={sxStyles.body}>
        <View sx={sxStyles.topHeader}>
          <Icon name="stepTwo" height={40} width={40} />
          <View sx={sxStyles.textMb}>
            <H6 bold>{t('unionPay::payment-instruction.title--recipient-information')}</H6>
            <TextBody>{t('unionPay::payment-instruction.text--debit-amount')}</TextBody>
          </View>
        </View>
        <View sx={{ gap: 20 }}>
          <View sx={{ gap: 4 }}>
            <TextCaption semiBold>{t('unionPay::payment-instruction.text--unionpay-account')}</TextCaption>
            <Pressable
              disabled={!transferPeerCountFlag}
              onPress={() => {
                navigationAny.navigate(navigationNavigate, {
                  screen: navigationScreen,
                  params: {
                    screen: UnionPayStackRoutes.SelectAccount,
                    onLayout,
                    onBack,
                    onSelect: (data: any) => {
                      setBeneficiaryInformation({ ...beneficiaryInformation, account: data });
                      setAccountData(data);
                    },
                    onChangeView: onChangeViewLocal
                  }
                });
              }}>
              <View sx={{ ...sxStyles.account, ...(accountData ? { flexDirection: 'column', py: '$2' } : {}) }}>
                <View
                  sx={{
                    justifyContent: 'space-between',
                    display: 'flex',
                    width: '100%',
                    flexDirection: 'row',
                    alignItems: 'center'
                  }}>
                  {accountData ? (
                    <View sx={{ gap: '$1' }}>
                      <TextBody semiBold sx={{ color: '$textPrimary' }}>
                        {accountData?.accountLabel}
                      </TextBody>
                      <TextBody sx={{ color: '$textPrimary' }}>{accountData?.accountNumber}</TextBody>
                    </View>
                  ) : (
                    <TextCaption sx={{ color: '$borderUpload' }}>{t('unionPay::payment-instruction.text--select-account')}</TextCaption>
                  )}
                  <Icon name="arrowRight" />
                </View>
              </View>
            </Pressable>
            <TextCaption secondary>{t('unionPay::payment-instruction.text--make-sure-accurate-information')}</TextCaption>
          </View>
          <View sx={{ ...sxStyles.dropdown, zIndex: 100 }}>
            <View sx={{ zIndex: 9999 }}>
              <Dropdown
                label={t('unionPay::payment-instruction.text--purpose')}
                placeholder={t('unionPay::payment-instruction.text--select-purpose')}
                options={RemittancePurposeSelectFilters(t)}
                sx={{ width: '100%' }}
                onValueChange={(value) => {
                  handleChangeButtonProps('size', value as ButtonViewProps['size']);
                  setBeneficiaryInformation({ ...beneficiaryInformation, purpose: isString(value) ? value : '' });
                }}
              />
            </View>
            <View sx={{ zIndex: 9888 }}>
              <Dropdown
                label={t('unionPay::payment-instruction.text--fund-source')}
                placeholder={t('unionPay::payment-instruction.text--select-source')}
                options={FundSourceSelectFilters(t)}
                sx={{ width: '100%' }}
                onValueChange={(value) => {
                  handleChangeButtonProps('size', value as ButtonViewProps['size']);
                  setBeneficiaryInformation({ ...beneficiaryInformation, fundSource: isString(value) ? value : '' });
                }}
              />
            </View>
            <UniInput
              labelText={t('unionPay::payment-instruction.text--remarks-optional')}
              multiline
              textAlignVertical="top"
              sx={{ height: 68, alignItems: 'flex-start' }}
              inputSx={{ height: 68, lineHeight: 34 }}
              onValueChange={(value) => setBeneficiaryInformation({ ...beneficiaryInformation, remark: value })}
            />
          </View>
          {viewShow && <View sx={{ height: '$1', width: '100%' }} />}
          <View sx={{ ...sxStyles.btnView, zIndex: 10, flexDirection: 'row', gap: '$2' }}>
            <UniButton
              wrapperSx={{ width: '48%' }}
              variant={ButtonVariant.outline1}
              labelSx={{ color: '$textPrimary' }}
              sx={{ width: '100%', height: 44 }}
              label={t('global-bill-payment::glossary.back')}
              onPress={onCancel}
            />
            <UniButton
              wrapperSx={{ width: '48%' }}
              labelSx={{ color: '$balck' }}
              sx={{ height: 44 }}
              label={t('unionPay::payment-instruction.button--submit')}
              disabled={
                beneficiaryInformation.account && beneficiaryInformation.purpose && beneficiaryInformation.fundSource ? false : true
              }
              onPress={validateSubmit}
            />
          </View>
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  scrollView: {
    padding: '$3'
  },
  modalHeader: {
    height: 70,
    justifyContent: 'center'
  },
  body: {
    padding: '$7'
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: '$7',
    backgroundColor: '$backgroundSecondary'
  },
  account: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    py: '$5',
    px: '$4',
    borderWidth: 1,
    borderColor: '#DCDCDC',
    borderRadius: 5
  },
  topHeader: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: '$5',
    gap: '$2'
  },
  textMb: {
    textAlign: 'center',
    gap: 8
  },
  btnView: {
    width: '100%',
    paddingY: '$5'
  },
  dropdown: {
    gap: '$5'
  }
};
